<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useLocale } from 'forms/composables/useLocale';
import countries from 'shared/constants/countries.js';
import CountryCodeSelect from 'forms/components/CountryCodeSelect.vue';
import DocumentUploadField from 'forms/components/DocumentUploadField.vue';
import { loadGoogleMaps } from 'forms/utils/googleMaps';
import { parseGooglePlace } from 'forms/utils/parseGooglePlace';

const { t } = useLocale();

const props = defineProps({
  field: { type: Object, required: true },
  modelValue: { type: [String, Number, Boolean, File, Object], default: '' },
  error: { type: String, default: '' },
  // Locked field (e.g. an address part filled only via the Google search/map).
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'place-selected']);

const isDragging = ref(false);
const fileName = ref('');
const fileInputRef = ref(null);

// Optional per-field help revealed by a "?" badge next to the label.
const showTooltip = ref(false);
function toggleTooltip() {
  showTooltip.value = !showTooltip.value;
}

const fieldType = computed(() => props.field.type || 'text');
const isTextField = computed(() =>
  ['text', 'email', 'number', 'date', 'time', 'url'].includes(fieldType.value)
);
const isTel = computed(() => fieldType.value === 'tel');
const isTextarea = computed(() => fieldType.value === 'textarea');
const isSelect = computed(() => fieldType.value === 'select');
// A select rendered as a button group (radio cards) instead of a dropdown.
// Same value semantics as a select, so branching/conditions are unaffected.
const isButtonSelect = computed(
  () => isSelect.value && props.field.variant === 'buttons'
);
// Button layout: 'horizontal' (default, side by side) or 'vertical' (stacked).
const isVerticalButtons = computed(
  () => props.field.button_layout === 'vertical'
);
const isCheckbox = computed(() => fieldType.value === 'checkbox');
const isRadio = computed(() => fieldType.value === 'radio');
const isFile = computed(() => fieldType.value === 'file');
const isDocuments = computed(() => fieldType.value === 'documents');
const isAddress = computed(() => fieldType.value === 'address');

const inputClasses = computed(() => {
  const base =
    'block w-full rounded-lg border px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0';
  if (props.readonly) {
    return `${base} border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed`;
  }
  return props.error
    ? `${base} border-red-300 focus:border-red-400 focus:ring-red-200 bg-red-50`
    : `${base} border-slate-300 focus:border-woot-400 focus:ring-woot-200 bg-white`;
});

function onInput(event) {
  emit('update:modelValue', event.target.value);
}

// --- Phone (tel) with country dial code ---
const DEFAULT_COUNTRY_ID = 'MX';

const selectedCountryId = ref(
  props.field.default_country || DEFAULT_COUNTRY_ID
);
const nationalNumber = ref('');

const dialCode = computed(() => {
  const country = countries.find(c => c.id === selectedCountryId.value);
  return country ? country.dial_code : '';
});

// Split an incoming full value (e.g. "+525512345678") into country + number.
function parseTelValue(value) {
  const str = String(value ?? '').trim();
  if (!str) {
    nationalNumber.value = '';
    return;
  }
  if (str.startsWith('+')) {
    const match = countries
      .filter(c => c.dial_code && str.startsWith(c.dial_code))
      .sort((a, b) => b.dial_code.length - a.dial_code.length)[0];
    if (match) {
      selectedCountryId.value = match.id;
      nationalNumber.value = str.slice(match.dial_code.length).trim();
      return;
    }
  }
  nationalNumber.value = str;
}

function composedTelValue() {
  const national = nationalNumber.value.trim();
  return national ? `${dialCode.value}${national}` : '';
}

function emitTel() {
  emit('update:modelValue', composedTelValue());
}

function onTelCountrySelect(countryId) {
  selectedCountryId.value = countryId;
  emitTel();
}

function onTelNumberInput(event) {
  nationalNumber.value = event.target.value;
  emitTel();
}

if (isTel.value) {
  parseTelValue(props.modelValue);
}

// Re-sync when the value changes externally (e.g. URL prefill). After parsing,
// emit the normalized value so a prefill lacking a dial code still submits one.
watch(
  () => props.modelValue,
  value => {
    if (!isTel.value) return;
    if (value === composedTelValue()) return;
    parseTelValue(value);
    if (composedTelValue() !== value) emitTel();
  }
);

function onCheckboxChange(event) {
  emit('update:modelValue', event.target.checked);
}

function selectButton(value) {
  emit('update:modelValue', value);
}

function onFileChange(event) {
  const file = event.target.files?.[0];
  if (file) {
    fileName.value = file.name;
    emit('update:modelValue', file);
  }
}

function onDrop(event) {
  event.preventDefault();
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) {
    fileName.value = file.name;
    emit('update:modelValue', file);
  }
}

function onDragOver(event) {
  event.preventDefault();
  isDragging.value = true;
}

function onDragLeave() {
  isDragging.value = false;
}

function openFilePicker() {
  fileInputRef.value?.click();
}

function removeFile() {
  fileName.value = '';
  emit('update:modelValue', null);
}

// --- Address autocomplete (Google Places, new PlaceAutocompleteElement) ---
// The legacy `places.Autocomplete` is disabled for Google customers created
// after 2025-03-01, so we use the supported `PlaceAutocompleteElement` web
// component. It renders its own input into our container.
const addressContainerRef = ref(null);
const addressMapRef = ref(null);
const addressFallback = ref(false); // true → render a plain text input instead

// Opt-in interactive map (drag the pin to set the location → reverse geocode).
const showAddressMap = computed(
  () => isAddress.value && props.field.show_map === true
);
// Google search autocomplete is on by default for an address field; the builder
// can disable it (e.g. map-only). Independent from the map.
const autocompleteEnabled = computed(
  () => isAddress.value && props.field.autocomplete !== false
);

function onAddressInput(event) {
  emit('update:modelValue', event.target.value);
}

// Reverse-geocode a {lat,lng} and fill the address fields from the result.
async function reverseGeocodeAndFill(geocoder, latLng) {
  try {
    const { results } = await geocoder.geocode({ location: latLng });
    if (results && results[0]) {
      emit('update:modelValue', results[0].formatted_address || '');
      emit('place-selected', parseGooglePlace(results[0]));
    }
  } catch {
    /* ignore reverse-geocode failures */
  }
}

// Initial map view per country (centroid + country-level zoom) so the empty-state
// pin starts over the right country. Falls back to Mexico for unmapped codes.
const COUNTRY_MAP_VIEWS = {
  mx: { lat: 23.6345, lng: -102.5528, zoom: 4 },
  ve: { lat: 6.4238, lng: -66.5897, zoom: 6 }, // Venezuela, country-level
};
const DEFAULT_MAP_VIEW = COUNTRY_MAP_VIEWS.mx;

function countryMapView(code) {
  return COUNTRY_MAP_VIEWS[(code || '').toLowerCase()] || DEFAULT_MAP_VIEW;
}

// Build the small map + draggable marker; returns helpers to sync it from the
// autocomplete selection. Requires the Geocoding API enabled on the key.
async function initAddressMap(maps) {
  if (!addressMapRef.value) return null;
  const [{ Map }, { Geocoder }] = await Promise.all([
    maps.importLibrary('maps'),
    maps.importLibrary('geocoding'),
  ]);
  const view = countryMapView(props.field.country);
  const center = { lat: view.lat, lng: view.lng };
  const map = new Map(addressMapRef.value, {
    center,
    zoom: view.zoom,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
  });
  const marker = new maps.Marker({ map, position: center, draggable: true });
  marker.setVisible(false);
  const geocoder = new Geocoder();

  const place = latLng => {
    marker.setPosition(latLng);
    marker.setVisible(true);
    reverseGeocodeAndFill(geocoder, latLng.toJSON ? latLng.toJSON() : latLng);
  };
  map.addListener('click', e => place(e.latLng));
  marker.addListener('dragend', () =>
    reverseGeocodeAndFill(geocoder, marker.getPosition().toJSON())
  );

  return {
    moveTo(location, zoom = 16) {
      if (!location) return;
      map.setCenter(location);
      map.setZoom(zoom);
      marker.setPosition(location);
      marker.setVisible(true);
    },
  };
}

onMounted(() => {
  if (!isAddress.value) return;
  const wantSearch = autocompleteEnabled.value;
  const wantMap = showAddressMap.value;
  if (!wantSearch && !wantMap) return; // plain address text field

  const apiKey = window.globalConfig?.GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    if (wantSearch) addressFallback.value = true; // no key → plain text input
    return;
  }

  loadGoogleMaps(apiKey)
    .then(async maps => {
      // Build the map first (if requested) so the search can sync the pin.
      const mapApi = wantMap
        ? await initAddressMap(maps).catch(() => null)
        : null;

      if (!wantSearch) return;

      // loadGoogleMaps guarantees importLibrary is defined; load the places lib.
      const places = await maps.importLibrary('places');
      if (!addressContainerRef.value || !places?.PlaceAutocompleteElement) {
        addressFallback.value = true;
        return;
      }
      // Bias to precise street addresses so results include postal_code and
      // street_number (route/locality-level picks omit them). Overridable.
      const el = new places.PlaceAutocompleteElement({
        includedRegionCodes: [(props.field.country || 'mx').toLowerCase()],
        includedPrimaryTypes: props.field.included_types || [
          'street_address',
          'premise',
          'subpremise',
        ],
      });
      el.style.width = '100%';
      addressContainerRef.value.appendChild(el);

      el.addEventListener('gmp-select', async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'location'],
        });
        emit('update:modelValue', place.formattedAddress || '');
        emit('place-selected', parseGooglePlace(place));
        mapApi?.moveTo(place.location);
      });
    })
    .catch(() => {
      if (wantSearch) addressFallback.value = true;
    });
});
</script>

<template>
  <div>
    <!-- Label -->
    <label
      v-if="!isCheckbox"
      :for="field.name"
      class="block text-sm font-medium text-slate-700 mb-1.5"
    >
      {{ field.label }}
      <span v-if="field.required" class="text-red-400">*</span>
      <button
        v-if="field.tooltip"
        type="button"
        :aria-label="t('FIELD_INFO_LABEL')"
        :aria-expanded="showTooltip"
        class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full border border-slate-300 text-slate-400 text-[10px] font-semibold leading-none align-middle hover:border-woot-400 hover:text-woot-500 focus:outline-none focus:ring-2 focus:ring-woot-200"
        @click.prevent="toggleTooltip"
      >
        ?
      </button>
    </label>

    <!-- Tooltip help (toggled by the "?" badge) -->
    <p
      v-if="field.tooltip && showTooltip"
      class="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-2"
    >
      {{ field.tooltip }}
    </p>

    <!-- Help text above field -->
    <p v-if="field.helpText" class="text-xs text-slate-400 mb-1.5">
      {{ field.helpText }}
    </p>

    <!-- Text input -->
    <input
      v-if="isTextField"
      :id="field.name"
      :type="fieldType"
      :value="modelValue"
      :placeholder="field.placeholder || ''"
      :required="field.required"
      :readonly="readonly"
      :min="field.min"
      :max="field.max"
      :class="inputClasses"
      @input="onInput"
    />

    <!-- Address field: optional Google search + optional map, both configurable -->
    <div v-else-if="isAddress">
      <!-- Search: PlaceAutocompleteElement mounts here (when autocomplete is on) -->
      <div
        v-show="autocompleteEnabled && !addressFallback"
        ref="addressContainerRef"
        class="gmp-wrap"
      />
      <!-- Plain input when autocomplete is off, or no key / SDK fails -->
      <input
        v-if="!autocompleteEnabled || addressFallback"
        :id="field.name"
        type="text"
        autocomplete="off"
        :value="modelValue"
        :placeholder="field.placeholder || t('ADDRESS_SEARCH_PLACEHOLDER')"
        :required="field.required"
        :class="inputClasses"
        @input="onAddressInput"
      />
      <!-- Optional interactive map: click or drag the pin to set the location -->
      <div v-if="showAddressMap">
        <p class="text-xs text-slate-400 mt-2 mb-1">
          {{ t('ADDRESS_MAP_HINT') }}
        </p>
        <div
          ref="addressMapRef"
          class="h-48 w-full rounded-lg border border-slate-200 overflow-hidden"
        />
      </div>
    </div>

    <!-- Phone with country dial code -->
    <div v-else-if="isTel" class="flex gap-2">
      <CountryCodeSelect
        :model-value="selectedCountryId"
        :error="!!error"
        @update:model-value="onTelCountrySelect"
      />
      <input
        :id="field.name"
        type="tel"
        inputmode="tel"
        :value="nationalNumber"
        :placeholder="field.placeholder || ''"
        :required="field.required"
        :readonly="readonly"
        :class="inputClasses"
        class="flex-1 min-w-0"
        @input="onTelNumberInput"
      />
    </div>

    <!-- Textarea -->
    <textarea
      v-else-if="isTextarea"
      :id="field.name"
      :value="modelValue"
      :placeholder="field.placeholder || ''"
      :required="field.required"
      :readonly="readonly"
      :rows="field.rows || 4"
      :class="inputClasses"
      @input="onInput"
    />

    <!-- Select rendered as a button group (radio cards) -->
    <div
      v-else-if="isButtonSelect"
      role="radiogroup"
      :aria-label="field.label"
      class="flex gap-2"
      :class="isVerticalButtons ? 'flex-col' : 'flex-wrap'"
    >
      <button
        v-for="option in field.options || []"
        :key="option.value ?? option"
        type="button"
        role="radio"
        :aria-checked="modelValue === (option.value ?? option)"
        class="px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-woot-200"
        :class="[
          isVerticalButtons ? 'w-full text-center' : '',
          modelValue === (option.value ?? option)
            ? 'border-woot-500 bg-woot-50 text-woot-700 ring-1 ring-woot-300'
            : error
              ? 'border-red-300 bg-red-50 text-slate-700 hover:border-red-400'
              : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
        ]"
        @click="selectButton(option.value ?? option)"
      >
        {{ option.label ?? option }}
      </button>
    </div>

    <!-- Select -->
    <select
      v-else-if="isSelect"
      :id="field.name"
      :value="modelValue"
      :required="field.required"
      :disabled="readonly"
      :class="inputClasses"
      @change="onInput"
    >
      <option value="" disabled>
        {{ field.placeholder || t('FIELD_SELECT_PLACEHOLDER') }}
      </option>
      <option
        v-for="option in field.options || []"
        :key="option.value ?? option"
        :value="option.value ?? option"
      >
        {{ option.label ?? option }}
      </option>
    </select>

    <!-- Checkbox -->
    <label
      v-else-if="isCheckbox"
      :for="field.name"
      class="flex items-start gap-3 cursor-pointer group"
    >
      <input
        :id="field.name"
        type="checkbox"
        :checked="modelValue"
        :required="field.required"
        class="mt-0.5 h-4 w-4 rounded border-slate-300 text-woot-500 focus:ring-woot-400"
        @change="onCheckboxChange"
      />
      <span class="text-sm text-slate-700 group-hover:text-slate-800">
        {{ field.label }}
        <span v-if="field.required" class="text-red-400">*</span>
        <button
          v-if="field.tooltip"
          type="button"
          :aria-label="t('FIELD_INFO_LABEL')"
          :aria-expanded="showTooltip"
          class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full border border-slate-300 text-slate-400 text-[10px] font-semibold leading-none align-middle hover:border-woot-400 hover:text-woot-500 focus:outline-none focus:ring-2 focus:ring-woot-200"
          @click.prevent.stop="toggleTooltip"
        >
          ?
        </button>
      </span>
    </label>
    <p
      v-if="isCheckbox && field.tooltip && showTooltip"
      class="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-2"
    >
      {{ field.tooltip }}
    </p>

    <!-- Radio -->
    <fieldset v-else-if="isRadio">
      <div class="space-y-2">
        <label
          v-for="option in field.options || []"
          :key="option.value ?? option"
          class="flex items-center gap-3 cursor-pointer group"
        >
          <input
            type="radio"
            :name="field.name"
            :value="option.value ?? option"
            :checked="modelValue === (option.value ?? option)"
            :required="field.required"
            class="h-4 w-4 border-slate-300 text-woot-500 focus:ring-woot-400"
            @change="onInput"
          />
          <span class="text-sm text-slate-700 group-hover:text-slate-800">
            {{ option.label ?? option }}
          </span>
        </label>
      </div>
    </fieldset>

    <!-- File upload -->
    <div v-else-if="isFile">
      <div
        v-if="!fileName"
        class="border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer"
        :class="[
          isDragging
            ? 'border-woot-400 bg-woot-50'
            : error
              ? 'border-red-300 bg-red-50'
              : 'border-slate-300 hover:border-slate-400 bg-slate-50',
        ]"
        @drop="onDrop"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @click="openFilePicker"
      >
        <svg
          class="w-8 h-8 mx-auto mb-2 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p class="text-sm text-slate-600 mb-1">
          {{ t('FILE_DRAG_HINT') }}
          <span class="text-woot-500 font-medium">{{ t('FILE_SELECT_LABEL') }}</span>
        </p>
        <p v-if="field.accept" class="text-xs text-slate-400">
          Formatos: {{ field.accept }}
        </p>
        <input
          ref="fileInputRef"
          type="file"
          :accept="field.accept || '*'"
          class="hidden"
          @change="onFileChange"
        />
      </div>
      <!-- File selected preview -->
      <div
        v-else
        class="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3"
      >
        <svg
          class="w-5 h-5 text-slate-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
          />
        </svg>
        <span class="text-sm text-slate-700 truncate flex-1">{{
          fileName
        }}</span>
        <button
          type="button"
          class="p-1 text-slate-400 hover:text-red-500 transition-colors"
          @click="removeFile"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Documents: inline required-document uploads (DocumentRequest-backed) -->
    <DocumentUploadField
      v-else-if="isDocuments"
      :field="field"
      :model-value="modelValue || {}"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <!-- Error message -->
    <p v-if="error" class="mt-1.5 text-xs text-red-500">
      {{ error }}
    </p>
  </div>
</template>

<!--
  Global (non-scoped): the Google PlaceAutocompleteElement is inserted at runtime
  into .gmp-wrap and uses a closed shadow DOM, so we frame it at the host level to
  match the other inputs (border, rounded corners, white bg, focus ring).
-->
<style>
.gmp-wrap gmp-place-autocomplete {
  display: block;
  width: 100%;
  background: #fff;
  border: 1px solid #cbd5e1; /* slate-300 */
  border-radius: 0.5rem; /* rounded-lg */
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.gmp-wrap gmp-place-autocomplete:hover {
  border-color: #94a3b8; /* slate-400 */
}
.gmp-wrap gmp-place-autocomplete:focus-within {
  border-color: #6aa1ff;
  box-shadow: 0 0 0 2px rgba(106, 161, 255, 0.35);
  outline: none;
}
</style>
